import { isPlatformBrowser } from '@angular/common';
import {
  Directive,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
} from '@angular/core';

/**
 * Emits once when the host element first enters the viewport.
 * Use with `*ngIf` to defer rendering heavy sections/components until visible.
 */
@Directive({
  selector: '[appDeferLoad]',
  standalone: true,
})
export class DeferLoadDirective implements OnInit, OnDestroy {
  @Input() rootMargin = '250px 0px';
  @Input() threshold: number | number[] = 0.01;
  @Output() appDeferLoad = new EventEmitter<void>();

  private observer?: IntersectionObserver;
  private emitted = false;

  constructor(
    private host: ElementRef<HTMLElement>,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {}

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      // SSR: render immediately
      this.emitOnce();
      return;
    }

    if (typeof IntersectionObserver === 'undefined') {
      // Very old browsers: render immediately
      this.emitOnce();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          this.emitOnce();
          this.observer?.disconnect();
          this.observer = undefined;
        }
      },
      { root: null, rootMargin: this.rootMargin, threshold: this.threshold },
    );

    this.observer.observe(this.host.nativeElement);
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.observer = undefined;
  }

  private emitOnce(): void {
    if (this.emitted) return;
    this.emitted = true;
    this.appDeferLoad.emit();
  }
}
